import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import {
  addToBlacklist,
  getBlacklist,
  removeFromBlacklist,
  updateBlacklist,
} from 'lib/api/blackList';
import DocumentSearcher from 'lib/common/DocumentSearcher';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import {
  hasAnyPermissions,
  rutChanger,
  showAlert,
  validaRut,
} from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

interface BlacklistItem {
  id: number;
  businessName: string;
  businessIdentifier: string;
  reason: string;
}

const Blacklist = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [blacklist, setBlacklist] = useState<BlacklistItem[]>([]);
  const [currentBusinessName, setCurrentBusinessName] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [reason, setReason] = useState('');
  const [businessIdentifierSearch, setBusinessIdentifierSearch] = useState('');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editId, setEditId] = useState(0);
  const [newReason, setNewReason] = useState('');
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [removeReason, setRemoveReason] = useState('');
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    if (businessIdentifierSearch) {
      query = `businessIdentifier=${businessIdentifierSearch}&`;
    }
    if (page) query += `page=${page}&pageSize=${pageSize}`;
    return query;
  };

  const fetchBlacklist = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    setLoading(true);
    try {
      const query = generateQuery({ page, pageSize });
      const response = await getBlacklist(dispatch, query);
      setBlacklist(response.data);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setBusinessName('');
    setIdentifier('');
    setReason('');
  };

  const handleSubmit = async () => {
    if (!identifier || !businessName || !reason)
      return alert('Completa todos los campos');
    if (!validaRut(identifier)) {
      alert('Rut inválido');
      return;
    }
    setLoading(true);
    try {
      await addToBlacklist(dispatch, identifier, {
        reason,
        businessName,
      });
      await fetchBlacklist({ page: 0, pageSize: PAGE_SIZE });
      showAlert(dispatch, 'success', 'Empresa agregada a la blacklist');
      setLoading(false);
    } catch (error: any) {
      if (error?.response?.status === 400) {
        showAlert(dispatch, 'error', error?.response?.data);
      } else {
        showAlert(dispatch, 'error', 'Hubo un error');
      }
      setLoading(false);
    }
    handleCloseDialog();
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      await removeFromBlacklist(dispatch, identifier, { reason: removeReason });
      await fetchBlacklist({ page: 0, pageSize: PAGE_SIZE });
      showAlert(dispatch, 'success', 'Empresa eliminada de la blacklist');
    } catch (error) {
      showAlert(dispatch, 'error', 'Hubo un error');
    } finally {
      setLoading(false);
      setOpenRemoveDialog(false);
      setRemoveReason('');
    }
  };

  const handleEdit = async () => {
    setLoading(true);
    try {
      await updateBlacklist(dispatch, { id: editId, reason: newReason });
      await fetchBlacklist({ page: 0, pageSize: PAGE_SIZE });
      showAlert(dispatch, 'success', 'Razón actualizada');
    } catch (error) {
      showAlert(dispatch, 'error', 'Hubo un error');
    } finally {
      setLoading(false);
      handleCloseEditDialog();
    }
  };

  const handleOpenEditDialog = (id: number) => {
    setOpenEditDialog(true);
    setEditId(id);
    const blackListItem = blacklist.find(
      (item: BlacklistItem) => item.id === id,
    );
    setCurrentBusinessName(blackListItem?.businessName || '');
    setNewReason(blackListItem?.reason || '');
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setNewReason('');
  };

  useEffect(() => {
    void fetchBlacklist({ page: 0, pageSize: PAGE_SIZE });
  }, []);

  const startButtons: CustomButtonProps[] = [
    {
      action: () => setOpenDialog(true),
      name: 'Agregar',
      color: 'primary',
      disabled: !hasAnyPermissions(user, [
        'settings',
        'settings_risk',
        'settings_risk_blacklist_add',
      ]),
    },
    {
      name: 'Filtro',
      action: () => {},
      color: 'primary',
      customButton: (
        <DocumentSearcher
          searchData={fetchBlacklist}
          setData={setBlacklist}
          setLoading={setLoading}
          properties={[
            {
              label: 'RUT Empresa',
              value: businessIdentifierSearch,
              setter: setBusinessIdentifierSearch,
              type: 'text',
            },
          ]}
          pagination={pagination}
        />
      ),
    },
  ];

  const handleDelete = (id: number) => {
    const blackListItem = blacklist.find(
      (item: BlacklistItem) => item.id === id,
    );
    setCurrentBusinessName(blackListItem?.businessName || '');
    setIdentifier(blackListItem?.businessIdentifier || '');
    setOpenRemoveDialog(true);
  };

  const actions = [
    {
      action: handleDelete,
      actionLabel: 'Eliminar',
      canShow: (_e: string | number) =>
        hasAnyPermissions(user, [
          'settings',
          'settings_risk',
          'settings_risk_blacklist_delete',
        ]),
    },
    {
      action: handleOpenEditDialog,
      actionLabel: 'Editar',
      canShow: (_e: string | number) =>
        hasAnyPermissions(user, [
          'settings',
          'settings_risk',
          'settings_risk_blacklist_edit',
        ]),
    },
  ];

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        variant="body1"
        subHeader
        buttons={startButtons}
        title="Acá encontrarás todas las empresas que estén dentro de tu blacklist"
      />
      <EnhancedTable
        dataHeaders={[
          { key: 'businessName', label: 'Nombre empresa' },
          { key: 'businessIdentifier', label: 'RUT' },
          { key: 'reason', label: 'Razón' },
        ]}
        actions={actions}
        data={blacklist}
        rowsPerPageDefault={10}
        exportToFile="xlsx"
      />
      {loading && <CenteredCircularProgress relative />}
      <Dialog
        open={openDialog}
        onClose={() => handleCloseDialog()}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">Agregar empresa</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Agrega una empresa a tu blacklist
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            id="businessName"
            label="Nombre de la empresa"
            type="text"
            fullWidth
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
          />
          <TextField
            autoFocus
            margin="dense"
            id="identifier"
            label="RUT de la empresa"
            type="text"
            fullWidth
            value={identifier}
            onChange={(e) => rutChanger(e.target.value, setIdentifier)}
          />
          <TextField
            autoFocus
            margin="dense"
            id="reason"
            label="Razón"
            type="text"
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <CustomButton onClick={() => setOpenDialog(false)} color="secondary">
            Cancelar
          </CustomButton>
          <CustomButton onClick={() => handleSubmit()} color="primary">
            Añadir
          </CustomButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openEditDialog}
        onClose={() => handleCloseEditDialog()}
        aria-labelledby="form-dialog-title"
        maxWidth="lg"
      >
        <DialogTitle id="form-dialog-title">
          Editar razón de {currentBusinessName}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="newReason"
            label="Razón"
            type="text"
            fullWidth
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
            multiline
          />
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={() => handleCloseEditDialog()}
            color="secondary"
          >
            Cancelar
          </CustomButton>
          <CustomButton onClick={() => handleEdit()} color="primary">
            Guardar
          </CustomButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openRemoveDialog}
        onClose={() => setOpenRemoveDialog(false)}
        aria-labelledby="remove-dialog-title"
        maxWidth="sm"
      >
        <DialogTitle id="remove-dialog-title">BlackList</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Estás seguro de querer eliminar a {currentBusinessName} de la
            blacklist? De ser así, escribe la razón para eliminar.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            id="removeReason"
            label="Razón para eliminar"
            type="text"
            fullWidth
            value={removeReason}
            onChange={(e) => setRemoveReason(e.target.value)}
            multiline
          />
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={() => setOpenRemoveDialog(false)}
            color="secondary"
            disabled={loading}
          >
            Cancelar
          </CustomButton>
          <CustomButton
            onClick={() => handleConfirmDelete()}
            color="primary"
            disabled={loading || !removeReason}
          >
            Eliminar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Blacklist;
